'use client';
import React, { useState, useRef, useCallback, useEffect } from 'react';

interface ImageItem {
  id: string;
  file: File;
  url: string;
}

type Direction = 'horizontal' | 'vertical';
type Format = 'png' | 'jpg';

export default function MergeImagesTool() {
  const [images, setImages] = useState<ImageItem[]>([]);
  const [direction, setDirection] = useState<Direction>('vertical');
  const [gap, setGap] = useState(10);
  const [bgColor, setBgColor] = useState('#ffffff');
  const [result, setResult] = useState<string | null>(null);
  const [resultFormat, setResultFormat] = useState<Format>('png');
  const [downloadFormat, setDownloadFormat] = useState<Format>('png');
  const [processing, setProcessing] = useState(false);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const dragItemId = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateId = () => Math.random().toString(36).slice(2, 9);

  const addImages = (files: FileList | null) => {
    if (!files) return;
    const remaining = 10 - images.length;
    if (remaining <= 0) return;
    const newImgs = Array.from(files)
      .filter((f) => f.type.startsWith('image/'))
      .slice(0, remaining)
      .map((f) => ({ id: generateId(), file: f, url: URL.createObjectURL(f) }));
    setImages((prev) => [...prev, ...newImgs]);
    setResult(null);
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item) URL.revokeObjectURL(item.url);
      return prev.filter((i) => i.id !== id);
    });
    setResult(null);
  };

  // Drag and drop reorder
  const handleDragStart = (id: string) => {
    dragItemId.current = id;
  };

  const handleDragOver = useCallback((e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDragOverId(id);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    const sourceId = dragItemId.current;
    if (!sourceId || sourceId === targetId) {
      setDragOverId(null);
      return;
    }
    setImages((prev) => {
      const arr = [...prev];
      const fromIdx = arr.findIndex((i) => i.id === sourceId);
      const toIdx = arr.findIndex((i) => i.id === targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
    setDragOverId(null);
    setResult(null);
  }, []);

  const handleDragEnd = () => {
    dragItemId.current = null;
    setDragOverId(null);
  };

  const handleDrop_zone = (e: React.DragEvent) => {
    e.preventDefault();
    addImages(e.dataTransfer.files);
  };

  const handleMerge = async () => {
    if (images.length < 2) return;
    setProcessing(true);
    try {
      const loadedImgs = await Promise.all(
        images.map(
          (item) =>
            new Promise<HTMLImageElement>((resolve, reject) => {
              const img = new window.Image();
              img.onload = () => resolve(img);
              img.onerror = reject;
              img.src = item.url;
            })
        )
      );

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');

      if (direction === 'horizontal') {
        canvas.width =
          loadedImgs.reduce((sum, img) => sum + img.naturalWidth, 0) +
          gap * (loadedImgs.length - 1);
        canvas.height = Math.max(...loadedImgs.map((img) => img.naturalHeight));
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        let x = 0;
        for (const img of loadedImgs) {
          ctx.drawImage(img, x, 0);
          x += img.naturalWidth + gap;
        }
      } else {
        canvas.width = Math.max(...loadedImgs.map((img) => img.naturalWidth));
        canvas.height =
          loadedImgs.reduce((sum, img) => sum + img.naturalHeight, 0) +
          gap * (loadedImgs.length - 1);
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        let y = 0;
        for (const img of loadedImgs) {
          ctx.drawImage(img, 0, y);
          y += img.naturalHeight + gap;
        }
      }

      const fmt = downloadFormat === 'jpg' ? 'image/jpeg' : 'image/png';
      setResult(canvas.toDataURL(fmt, 0.92));
      setResultFormat(downloadFormat);
    } catch (err) {
      console.error(err);
      alert('Failed to merge images. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = (fmt: Format) => {
    if (!result) return;
    // Re-render if format differs from current result
    if (fmt !== resultFormat) {
      // Trigger re-merge with new format
      setDownloadFormat(fmt);
      return;
    }
    const a = document.createElement('a');
    a.href = result;
    a.download = `merged_images.${fmt}`;
    a.click();
  };

  // When downloadFormat changes and result exists, re-merge
  React.useEffect(() => {
    if (result && images.length >= 2) {
      handleMerge();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadFormat]);

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div
        className="upload-zone rounded-2xl p-8 text-center cursor-pointer transition-all"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop_zone}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-1">
            <svg className="w-6 h-6 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary">Click or drag images here</p>
          <p className="text-text-muted text-sm">JPG, PNG, WebP — up to 10 images</p>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => addImages(e.target.files)}
        />
      </div>

      {/* Image List with Drag-and-Drop Reorder */}
      {images.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-text-primary">
              Images ({images.length}/10)
              <span className="ml-2 text-text-muted font-normal">— drag to reorder</span>
            </p>
            {images.length < 10 && (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="text-xs text-brand-blue font-semibold hover:underline"
              >
                + Add more
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div
                key={img.id}
                draggable
                onDragStart={() => handleDragStart(img.id)}
                onDragOver={(e) => handleDragOver(e, img.id)}
                onDrop={(e) => handleDrop(e, img.id)}
                onDragEnd={handleDragEnd}
                className={`relative group cursor-grab active:cursor-grabbing transition-all ${
                  dragOverId === img.id ? 'scale-105 ring-2 ring-brand-blue ring-offset-1 rounded-xl' : ''
                }`}
              >
                <div className="w-20 h-20 rounded-xl overflow-hidden border border-gray-100 bg-gray-50">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={img.url} alt={`Image ${i + 1}`} className="w-full h-full object-cover" />
                </div>
                {/* Order badge */}
                <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-brand-blue text-white text-xs font-bold flex items-center justify-center shadow">
                  {i + 1}
                </div>
                {/* Remove button */}
                <button
                  onClick={() => removeImage(img.id)}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                  title="Remove"
                >
                  ×
                </button>
                {/* Drag handle indicator */}
                <div className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-60 transition-opacity">
                  <svg className="w-3 h-3 text-white drop-shadow" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 6a2 2 0 100-4 2 2 0 000 4zm8 0a2 2 0 100-4 2 2 0 000 4zM8 14a2 2 0 100-4 2 2 0 000 4zm8 0a2 2 0 100-4 2 2 0 000 4zM8 22a2 2 0 100-4 2 2 0 000 4zm8 0a2 2 0 100-4 2 2 0 000 4z" />
                  </svg>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Direction */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">Merge Direction</label>
          <div className="flex gap-2">
            {(['vertical', 'horizontal'] as Direction[]).map((d) => (
              <button
                key={d}
                onClick={() => { setDirection(d); setResult(null); }}
                className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all border ${
                  direction === d
                    ? 'bg-brand-blue text-white border-brand-blue' :'bg-white text-text-secondary border-gray-200 hover:bg-gray-50'
                }`}
              >
                {d === 'horizontal' ? '↔ Side by Side' : '↕ Stacked'}
              </button>
            ))}
          </div>
        </div>

        {/* Gap */}
        <div>
          <div className="flex justify-between mb-2">
            <label className="text-sm font-semibold text-text-primary">Spacing</label>
            <span className="text-brand-blue text-sm font-bold">{gap}px</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={gap}
            onChange={(e) => { setGap(Number(e.target.value)); setResult(null); }}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: '#2563EB' }}
          />
          <div className="flex justify-between text-xs text-text-muted mt-1">
            <span>0px</span><span>100px</span>
          </div>
        </div>

        {/* Background Color */}
        <div>
          <label className="block text-sm font-semibold text-text-primary mb-2">Background Color</label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={bgColor}
              onChange={(e) => { setBgColor(e.target.value); setResult(null); }}
              className="w-10 h-10 rounded-xl border border-gray-200 cursor-pointer p-0.5"
            />
            <input
              type="text"
              value={bgColor}
              onChange={(e) => {
                const v = e.target.value;
                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) { setBgColor(v); setResult(null); }
              }}
              className="flex-1 px-3 py-2 rounded-xl border border-gray-200 text-sm font-mono text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-blue/20"
              maxLength={7}
            />
          </div>
          {/* Quick presets */}
          <div className="flex gap-1.5 mt-2">
            {['#ffffff', '#000000', '#f3f4f6', '#transparent'].map((c) => (
              <button
                key={c}
                onClick={() => {
                  const color = c === '#transparent' ? '#00000000' : c;
                  setBgColor(c === '#transparent' ? '#ffffff' : c);
                  setResult(null);
                }}
                title={c === '#transparent' ? 'White' : c}
                className="w-6 h-6 rounded-lg border border-gray-200 transition-transform hover:scale-110"
                style={{ backgroundColor: c === '#transparent' ? '#ffffff' : c }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Download Format */}
      <div>
        <label className="block text-sm font-semibold text-text-primary mb-2">Output Format</label>
        <div className="flex gap-2 w-fit">
          {(['png', 'jpg'] as Format[]).map((f) => (
            <button
              key={f}
              onClick={() => setDownloadFormat(f)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                downloadFormat === f
                  ? 'bg-brand-blue text-white border-brand-blue' :'bg-white text-text-secondary border-gray-200 hover:bg-gray-50'
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </div>
        {downloadFormat === 'jpg' && (
          <p className="text-xs text-amber-600 mt-1.5">JPG does not support transparency — background color will be applied.</p>
        )}
      </div>

      {/* Merge Button */}
      <button
        onClick={handleMerge}
        disabled={images.length < 2 || processing}
        className="btn-primary w-full text-base py-3.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {processing ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Merging…
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Merge {images.length >= 2 ? `${images.length} Images` : 'Images'}
          </>
        )}
      </button>

      {images.length < 2 && images.length > 0 && (
        <p className="text-center text-sm text-amber-600">Add at least 2 images to merge.</p>
      )}

      {/* Result Preview + Download */}
      {result && (
        <div className="space-y-4">
          <div className="rounded-2xl overflow-hidden border border-gray-100 bg-gray-50">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="text-sm font-semibold text-text-primary">Preview</p>
              <span className="text-xs text-text-muted bg-gray-100 px-2 py-0.5 rounded-full uppercase font-mono">
                {resultFormat}
              </span>
            </div>
            <div className="p-4 overflow-auto max-h-[500px]">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={result} alt="Merged result" className="max-w-full mx-auto rounded-xl shadow-sm" />
            </div>
          </div>

          {/* Download buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => {
                const a = document.createElement('a');
                a.href = result;
                a.download = `merged_images.${resultFormat}`;
                a.click();
              }}
              className="flex-1 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold text-sm transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
              </svg>
              Download as {resultFormat.toUpperCase()}
            </button>
            <button
              onClick={() => {
                const otherFmt: Format = resultFormat === 'png' ? 'jpg' : 'png';
                setDownloadFormat(otherFmt);
              }}
              className="px-4 py-3 rounded-xl border border-gray-200 text-text-secondary hover:bg-gray-50 font-semibold text-sm transition-colors"
            >
              Save as {resultFormat === 'png' ? 'JPG' : 'PNG'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
